import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "./use-auth";
import { apiClient } from "@/lib/api-client";
import type { GmailThreadPreview } from "@/api/generated/client/models";

// Re-export types from the generated API client
export type {
  GmailMessagePreview,
  GmailThreadPreview,
  SearchGmailThreadsResponse,
  ImportGmailThreadResponse,
} from "@/api/generated/client/models";

/**
 * Hook for Gmail integration: status, connect, disconnect.
 * Used on the Settings > Email tab.
 */
export function useGmail(options?: { includeSignature?: boolean }) {
  const includeSignature = options?.includeSignature ?? false;
  const { firebaseUser } = useAuth();
  const queryClient = useQueryClient();

  // Signature-enabled status calls trigger an additional Gmail API lookup on the
  // backend, so keep this query warm for a short window and avoid automatic
  // focus remount refetch churn in high-traffic screens like Claim Email.
  const statusStaleTimeMs = includeSignature ? 5 * 60 * 1000 : 60 * 1000;

  // Holds the teardown for an in-flight OAuth popup flow so we can also run it
  // if the component using this hook unmounts mid-connect (otherwise the 1s
  // poll interval, 5-min timeout, and message listener leak).
  const oauthCleanupRef = useRef<(() => void) | null>(null);
  useEffect(() => {
    // Tear down any in-flight OAuth popup flow on unmount. We intentionally read
    // the latest ref value here (it holds a callback, not a DOM node).
    const runCleanup = () => oauthCleanupRef.current?.();
    return runCleanup;
  }, []);

  const { data: status, isLoading } = useQuery({
    queryKey: ["gmail-status", includeSignature],
    queryFn: async () => {
      return await apiClient.gmail.getGmailStatus({ includeSignature });
    },
    enabled: !!firebaseUser,
    staleTime: statusStaleTimeMs,
    gcTime: statusStaleTimeMs * 2,
    refetchOnWindowFocus: includeSignature ? false : true,
    refetchOnMount: includeSignature ? false : true,
  });

  const connectMutation = useMutation({
    mutationFn: async (redirectUrl?: string): Promise<{ email?: string }> => {
      const response = await apiClient.gmail.getGmailAuthUrl({ redirectUrl });

      // Open OAuth in a popup window instead of redirecting the main app
      const width = 600;
      const height = 700;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;

      const popup = window.open(
        response.url,
        'gmail-oauth',
        `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no`
      );

      if (!popup) {
        throw new Error('Popup blocked. Please allow popups for this site.');
      }

      // The popup navigates to /oauth-callback.html, which postMessages the
      // result back to this (opener) window. We resolve/reject off that message.
      return new Promise<{ email?: string }>((resolve, reject) => {
        // Tear down every listener/timer in one place so no branch leaves a
        // dangling interval or timeout running after the flow settles.
        const cleanup = () => {
          window.removeEventListener('message', messageHandler);
          clearInterval(checkInterval);
          clearTimeout(timeoutId);
          oauthCleanupRef.current = null;
        };
        // Expose the teardown so the hook's unmount effect can also run it.
        oauthCleanupRef.current = cleanup;

        const messageHandler = (event: MessageEvent) => {
          // Verify the message is from our domain
          if (event.origin !== window.location.origin) {
            return;
          }

          if (event.data?.type === 'GMAIL_OAUTH_SUCCESS') {
            cleanup();
            if (!popup.closed) popup.close();
            resolve({ email: event.data?.email });
          } else if (event.data?.type === 'GMAIL_OAUTH_ERROR') {
            cleanup();
            if (!popup.closed) popup.close();
            reject(new Error(event.data.error || 'OAuth failed'));
          }
        };

        window.addEventListener('message', messageHandler);

        // Detect the user closing the popup manually (cancelled flow).
        const checkInterval = setInterval(() => {
          if (popup.closed) {
            cleanup();
            reject(new Error('Connection cancelled.'));
          }
        }, 1000);

        // Give up after 5 minutes so a stuck popup can't hang forever.
        const timeoutId = setTimeout(() => {
          cleanup();
          if (!popup.closed) popup.close();
          reject(new Error('Connection timed out. Please try again.'));
        }, 300000);
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["gmail-status"] });
    },
  });

  const disconnectMutation = useMutation({
    mutationFn: async () => {
      return await apiClient.gmail.disconnectGmail();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["gmail-status"] });
    },
  });

  return {
    connected: status?.connected ?? false,
    account: status?.account ?? null,
    composeSignatureText: status?.composeSignatureText ?? null,
    isLoading,
    connect: connectMutation.mutateAsync,
    isConnecting: connectMutation.isPending,
    disconnect: disconnectMutation.mutateAsync,
    isDisconnecting: disconnectMutation.isPending,
  };
}

/**
 * Hook for claim emails: list, send, get by ID.
 * Used on the claim detail email module.
 */
export function useClaimEmails(claimId: number) {
  const { firebaseUser } = useAuth();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["claim-emails", claimId],
    queryFn: async () => {
      if (!claimId) return { emails: [], count: 0, total: 0 };
      // Request a large page (200) so we rarely truncate; the backend caps at
      // 1000 and `total` still tells us when there are more than we fetched.
      return await apiClient.gmail.listClaimEmails({ claimId, limit: 200 });
    },
    enabled: !!claimId && !!firebaseUser,
  });

  const sendMutation = useMutation({
    mutationFn: async (params: {
      subject: string;
      to: Array<{ email: string; name?: string }>;
      cc?: Array<{ email: string; name?: string }>;
      bcc?: Array<{ email: string; name?: string }>;
      bodyText?: string;
      bodyHtml?: string;
      attachments?: Array<{ filename: string; mimeType: string; contentBase64: string }>;
      inReplyToGmailThreadId?: string;
    }) => {
      return await apiClient.gmail.sendClaimEmail({
        claimId,
        sendClaimEmailRequest: params,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["claim-emails", claimId] });
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
    },
  });

  const syncMutation = useMutation({
    mutationFn: async () => {
      return await apiClient.gmail.syncClaimEmails({ claimId });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["claim-emails", claimId] });
      queryClient.invalidateQueries({ queryKey: ["claim-activity", claimId] });
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (emailId: number) => {
      await apiClient.gmail.deleteClaimEmail({ claimId, emailId });
      return true;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["claim-emails", claimId] });
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
    },
  });

  const getEmailAttachmentBlob = async (emailId: number, attachmentId: number) => {
    return await apiClient.gmail.getClaimEmailAttachmentContent({ claimId, emailId, attachmentId });
  };

  return {
    emails: data?.emails ?? [],
    count: data?.count ?? 0,
    total: data?.total ?? 0,
    isLoading,
    sendEmail: sendMutation.mutateAsync,
    isSending: sendMutation.isPending,
    syncEmails: syncMutation.mutateAsync,
    deleteEmail: deleteMutation.mutate,
    deleteEmailAsync: deleteMutation.mutateAsync,
    isDeleting: deleteMutation.isPending,
    getEmailAttachmentBlob,
  };
}

/**
 * Hook for managing a claim's email auto-import rules (watched addresses and
 * subject terms). Rules take effect on the next email sync.
 */
export function useEmailMatchRules(claimId: number, enabled = true) {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["email-match-rules", claimId],
    queryFn: async () => {
      return await apiClient.gmail.listEmailMatchRules({ claimId });
    },
    enabled: !!claimId && enabled,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["email-match-rules", claimId] });

  const createMutation = useMutation({
    mutationFn: async (input: { ruleType: "email" | "subject"; value: string }) => {
      return await apiClient.gmail.createEmailMatchRule({
        claimId,
        createEmailMatchRuleRequest: input,
      });
    },
    onSuccess: invalidate,
  });

  const deleteMutation = useMutation({
    mutationFn: async (ruleId: number) => {
      await apiClient.gmail.deleteEmailMatchRule({ claimId, ruleId });
    },
    onSuccess: invalidate,
  });

  return {
    rules: data?.rules ?? [],
    isLoading,
    createRule: createMutation.mutateAsync,
    isCreating: createMutation.isPending,
    deleteRule: deleteMutation.mutateAsync,
    isDeletingRule: deleteMutation.isPending,
  };
}

/**
 * Hook for searching Gmail threads.
 * Used in the ImportGmailThreadModal to find threads to import.
 * Requires claimId for claim-scoped deduplication checking.
 */
export function useGmailSearch(claimId: number) {
  const searchMutation = useMutation({
    mutationFn: async (params: { query: string; maxResults?: number; pageToken?: string }) => {
      return await apiClient.gmail.searchGmailThreads({
        claimId,
        q: params.query,
        maxResults: params.maxResults,
        pageToken: params.pageToken,
      });
    },
  });

  return {
    search: searchMutation.mutateAsync,
    isSearching: searchMutation.isPending,
    results: searchMutation.data ?? null,
    error: searchMutation.error,
    reset: searchMutation.reset,
  };
}

/**
 * Hook for importing a Gmail thread (or specific messages) into a claim.
 */
export function useGmailImport(claimId: number) {
  const queryClient = useQueryClient();

  const importMutation = useMutation({
    mutationFn: async (params: { threadId: string; messageIds?: string[] }) => {
      return await apiClient.gmail.importGmailThread({
        claimId,
        importGmailThreadRequest: params,
      });
    },
    onSuccess: () => {
      // Refresh the claim emails list after importing
      queryClient.invalidateQueries({ queryKey: ["claim-emails", claimId] });
      queryClient.invalidateQueries({ queryKey: ["claim-activity", claimId] });
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
    },
  });

  return {
    importThread: importMutation.mutateAsync,
    isImporting: importMutation.isPending,
    result: importMutation.data ?? null,
    error: importMutation.error,
    reset: importMutation.reset,
  };
}

/**
 * Selection state for the Gmail import modal.
 *
 * A user can select whole threads (import every not-yet-imported message in
 * them) and/or individual messages within a thread.
 */
export interface SelectionState {
  /** Fully-selected thread IDs (import entire thread). */
  threads: Set<string>;
  /** Individually-selected message IDs, keyed by thread ID. */
  messages: Map<string, Set<string>>; // threadId -> Set<messageId>
}

/**
 * Encapsulates thread/message selection for the Gmail import modal.
 *
 * Rules:
 * - Selecting a whole thread imports every not-yet-imported message in it.
 * - Selecting individual messages imports just those.
 * - Toggling a single message inside a fully-selected thread "explodes" the
 *   thread selection into its individual messages (minus the one deselected).
 *
 * `threads` (the current search results) is needed to count selected messages
 * and to expand a thread selection into individual messages.
 */
export function useThreadSelection(threads: GmailThreadPreview[]) {
  const [selection, setSelection] = useState<SelectionState>({
    threads: new Set(),
    messages: new Map(),
  });

  const toggleThread = useCallback((threadId: string) => {
    setSelection((prev) => {
      const next = {
        threads: new Set(prev.threads),
        messages: new Map(prev.messages),
      };
      if (next.threads.has(threadId)) {
        next.threads.delete(threadId);
      } else {
        next.threads.add(threadId);
        // Whole thread selected — drop any individual message selections for it.
        next.messages.delete(threadId);
      }
      return next;
    });
  }, []);

  const toggleMessage = useCallback(
    (threadId: string, messageId: string) => {
      setSelection((prev) => {
        const next = {
          threads: new Set(prev.threads),
          messages: new Map(prev.messages),
        };

        // If whole thread is selected, deselect it and select all EXCEPT this message
        if (next.threads.has(threadId)) {
          next.threads.delete(threadId);
          const thread = threads.find((t) => t.threadId === threadId);
          if (thread) {
            const allMsgIds = new Set(
              thread.messages
                .filter((m) => m.messageId !== messageId && !m.alreadyImported)
                .map((m) => m.messageId)
            );
            next.messages.set(threadId, allMsgIds);
          }
          return next;
        }

        const msgSet = new Set(next.messages.get(threadId) ?? []);
        if (msgSet.has(messageId)) {
          msgSet.delete(messageId);
        } else {
          msgSet.add(messageId);
        }

        if (msgSet.size === 0) {
          next.messages.delete(threadId);
        } else {
          next.messages.set(threadId, msgSet);
        }

        return next;
      });
    },
    [threads]
  );

  const resetSelection = useCallback(() => {
    setSelection({ threads: new Set(), messages: new Map() });
  }, []);

  const isThreadSelected = useCallback(
    (threadId: string): boolean => selection.threads.has(threadId),
    [selection]
  );

  const isMessageSelected = useCallback(
    (threadId: string, messageId: string): boolean => {
      if (selection.threads.has(threadId)) return true;
      return selection.messages.get(threadId)?.has(messageId) ?? false;
    },
    [selection]
  );

  const totalSelected = useMemo(() => {
    let count = 0;
    for (const threadId of selection.threads) {
      const thread = threads.find((t) => t.threadId === threadId);
      count += thread?.messages.filter((m) => !m.alreadyImported).length ?? 0;
    }
    for (const [, msgs] of selection.messages) {
      count += msgs.size;
    }
    return count;
  }, [selection, threads]);

  const hasSelection = selection.threads.size > 0 || selection.messages.size > 0;

  return {
    /** Raw selection state — used by the import routine to iterate selected units. */
    selection,
    toggleThread,
    toggleMessage,
    resetSelection,
    isThreadSelected,
    isMessageSelected,
    totalSelected,
    hasSelection,
  };
}
